import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Popover } from 'antd';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import { useTranslation } from 'react-i18next';
import type { ComparisonSelectionTotals } from '@/types/chat/chat';
import {
  EXCLUSION_KEYS,
  formatBytesExact,
  quotaBreakdown,
  type ComparisonQuota,
} from './quota';
import styles from './composer.module.css';

export interface QuotaBadgeHandle {
  /** Show the breakdown (a blocking reason's resolution). */
  open: () => void;
}

/**
 * "n / 10 files · x / 20 MB", always shown in comparison mode (spec §4),
 * red with text when over a limit. The popover breaks it down: every
 * document with its exact bytes, merged duplicates and exclusion reasons.
 */
const QuotaBadge = forwardRef<
  QuotaBadgeHandle,
  { quota: ComparisonQuota; totals: ComparisonSelectionTotals }
>(({ quota, totals }, ref) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  // The breakdown takes focus, so Escape and screen readers reach it.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => panel.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);
  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);
  const rows = quotaBreakdown(totals);
  const locale = i18n.language.startsWith('en') ? 'en-US' : 'ja-JP';
  const bytes = (value: number) =>
    t('v2.composer.bytesExact', { b: formatBytesExact(value, locale) });
  const reason = (value: string | null) =>
    value && EXCLUSION_KEYS[value] ? t(EXCLUSION_KEYS[value]) : (value ?? '');

  const content = (
    <div
      ref={panel}
      className={styles.quotaPanel}
      tabIndex={-1}
      onKeyDown={event => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        setOpen(false);
        trigger.current?.focus();
      }}
      data-testid="quota-breakdown"
      role="dialog"
      aria-label={t('v2.composer.quotaTitle')}
    >
      <strong>{t('v2.composer.quotaTitle')}</strong>
      <ul className={styles.quotaRows}>
        {rows.map(row => (
          <li
            key={`${row.state}-${row.id}`}
            className={styles.quotaRow}
            data-state={row.state}
          >
            <span className={styles.quotaName}>
              {row.name ?? t('v2.composer.unnamedSource', { id: row.id })}
              {row.state === 'duplicate' && row.duplicateOf ? (
                <span className={styles.quotaNote}>
                  {t('v2.composer.duplicateOf', {
                    name:
                      row.duplicateOf.name ??
                      t('v2.composer.unnamedSource', {
                        id: row.duplicateOf.id,
                      }),
                  })}
                </span>
              ) : null}
              {row.state === 'excluded' ? (
                <span className={styles.quotaNote}>
                  {t('v2.composer.excludedBecause', {
                    reason: reason(row.reason),
                  })}
                </span>
              ) : null}
            </span>
            <span className={styles.quotaBytes}>
              {row.bytes == null ? '—' : bytes(row.bytes)}
            </span>
          </li>
        ))}
      </ul>
      <div className={styles.quotaTotal} data-over={quota.over || undefined}>
        <span>
          {t('v2.composer.quotaFiles', {
            n: quota.files,
            max: quota.maxFiles,
          })}
        </span>
        <span>
          {bytes(quota.bytes)} / {bytes(quota.maxBytes)}
        </span>
      </div>
      {totals.duplicate_count ? (
        <span className={styles.quotaNote}>
          {t('v2.composer.quotaDup', { n: totals.duplicate_count })}
        </span>
      ) : null}
      {totals.excluded_count ? (
        <span className={styles.quotaNote}>
          {t('v2.composer.quotaExcluded', { n: totals.excluded_count })}
        </span>
      ) : null}
      <span className={styles.quotaNote}>{t('v2.composer.quotaUnit')}</span>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="topLeft"
      content={content}
    >
      <button
        ref={trigger}
        type="button"
        className={styles.quota}
        data-over={quota.over || undefined}
        aria-expanded={open}
        data-testid="comparison-quota"
      >
        {quota.over ? <ExclamationCircleOutlined aria-hidden /> : null}
        <span>
          {t('v2.composer.quota', {
            f: quota.files,
            maxFiles: quota.maxFiles,
            mb: quota.mb,
            maxMb: quota.maxMb,
          })}
        </span>
        {quota.over ? <span>· {t('v2.composer.overLimit')}</span> : null}
      </button>
    </Popover>
  );
});

QuotaBadge.displayName = 'QuotaBadge';

export default QuotaBadge;
